import React from 'react';
import styles from './SettingsPin.module.scss';
import { BasicCodeInput } from '../auth/login/CodeInput';
import { useValidateSettingsPinMutation } from '../../redux/services/userApi';
import { useTypedDispatch, useTypedSelector } from '../../redux/hooks';
import {selectCurrentUserId, selectSettingsPinLockedUntil, updateActivePage, userSlice} from '../../redux/slices';

export const SettingsPin = ({
    setApproved
}: {
    setApproved: (newApproved: boolean) => void;
}) => {
    const dispatch = useTypedDispatch();

    const [
        validateSettingsPin,
        { isLoading: validateSettingsPinLoading, isSuccess: validateSettingsPinSuccess }
    ] = useValidateSettingsPinMutation();

    const lockedUntilTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const lockedUntil = useTypedSelector(selectSettingsPinLockedUntil);

    const userUuid = useTypedSelector(selectCurrentUserId);

    const [codeInputs, setCodeInputs] = React.useState<string[]>(
        Array.from({ length: 4 }).map(() => '')
    );
    const [activeInput, setActiveInput] = React.useState<number | undefined>(0);
    const inputRefs = React.useRef<HTMLInputElement[]>([]);

    const pin = codeInputs.join('');

    const pinIsValid = pin.length === 4;

    React.useEffect(() => {
        if (!pinIsValid) return;

        validateSettingsPin({
            userUuid,
            pin
        });
    }, [pinIsValid, pin]);

    React.useEffect(() => {
        if (!lockedUntil) return;

        const timeoutLength = new Date(lockedUntil).getTime() - Date.now();

        if (timeoutLength <= 0) {
            dispatch(userSlice.actions.updateSettingsPinLockedUntil());
        } else {
            lockedUntilTimeoutRef.current = setTimeout(() => {
                dispatch(userSlice.actions.updateSettingsPinLockedUntil());
            }, timeoutLength);
        }
    }, [lockedUntil]);

    const isDisabled = !!lockedUntil || validateSettingsPinLoading;

	React.useEffect(() => {
        if (isDisabled) {
	        setActiveInput(undefined);

			return;
        };

        const lastActiveIndex = codeInputs.findIndex(input => input !== '');
        const focusIndex = lastActiveIndex !== -1 ? lastActiveIndex : 0;
        inputRefs.current[focusIndex]?.focus();
        setActiveInput(focusIndex);
    }, [isDisabled]);

	React.useEffect(() => {
		if (!validateSettingsPinSuccess) return;

		setApproved(true);

		// Guarantees it happens after setApproved is processed
		setTimeout(() => {
			dispatch(updateActivePage('settings'))

			// Reset approved state after changing pages
			setTimeout(() => {
				setApproved(false)
			})
		})
	}, [validateSettingsPinSuccess])

    return (
        <div className={styles.wrapper}>
            <div className={styles.content}>
                <h2>Verify your PIN</h2>
                <p>For security purposes, please verify your PIN.</p>
                <BasicCodeInput
                    disabled={isDisabled}
                    codeInputs={codeInputs}
                    numbersOnly
                    setCodeInputs={setCodeInputs}
                    activeInput={activeInput}
                    setActiveInput={setActiveInput}
                    loading={false}
                    inputsRefs={inputRefs}
                />
                {!!lockedUntil && (
                    <p>
                        Access is temporarily locked.
                        <br />
                        Contact your administrator for assistance.
                    </p>
                )}
            </div>
        </div>
    );
};
